import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  BrokerRes,
  CurrencyConfigRes,
  HoldingRes,
  IWatchListDataRes,
  UpdateKycStepsDto,
  UserDetailsRes,
  ReKycRes,
  NotificationRes,
  SalesforceDetailsRes,
  AddressRes,
} from "./api/generated";
import { useAppSelector } from "../store";

export interface Details {
  customerId: string;
  name: string;
  firstName: string;
  lastName: string;
  dob: string;
  email: string;
  phone: string;
  loginCount: number | undefined;
}

export interface StateDisclosureData {
  state: string;
  accepted: boolean;
  timestamp: number;
  ipAddress?: string;
  /** Set when terms are versioned; used to re-prompt when this changes. */
  disclosureVersion?: string;
}

export interface DepositDisclosureData {
  state: string;
  accepted: boolean;
  timestamp: number;
  ipAddress?: string;
  type: "Crypto" | "Fiat";
  /** Set when terms are versioned; used to re-prompt when this changes. */
  disclosureVersion?: string;
}

export interface IOverview {
  balance: number;
  pending: number;
}

export type RafTiers = "bronze" | "gold" | "silver" | "black";
export interface IRaf {
  tier: RafTiers;
  referredAmount: number;
  rafCode: string;
  rafOptedIn: boolean;
  reward: string;
  OLD_RAF_SYSTEM: boolean;
  isLoadingRaf: boolean;
}

export interface INotifications {
  notificationAmount: number;
  currentNotifications: NotificationRes[];
  previousNotifications: NotificationRes[];
  refetchNotifications: boolean;
}

export interface AccountLockRes {
  locked: boolean;
  lockType: string;
  reason: string;
  timestamp: number;
}

export interface UserState {
  details: Details;
  broker: BrokerRes;
  is2FAEnabled?: boolean;
  defaultCurrency: CurrencyConfigRes | { code: string };
  kycSteps: Partial<UpdateKycStepsDto>;
  watchlist?: IWatchListDataRes[];
  watchlistPreviousState?: IWatchListDataRes[]; // For rollback on error
  referralSignUpCode: string;
  userHoldings: HoldingRes[];
  isCustomerTalkingWithBroker: boolean;
  reKycDetails: ReKycRes;
  overview: IOverview;
  defaultTradeFee?: number;
  isUnverified: boolean;
  isPendingVerification: boolean;
  raf: IRaf;
  notifications: INotifications;
  isSubmitted: boolean;
  salesforceDetails: SalesforceDetailsRes;
  burlingBank: boolean;
  emailCode: string | null;
  code2fa: string | null;
  isEmailCodeSuccess: boolean;
  is2FASuccess: boolean;
  accountCreateTime: number | null;
  address: AddressRes;
  loginRiskSet: boolean;
  kycStatus: string;
  accountLock: AccountLockRes;
  stateDisclosures: StateDisclosureData[];
  depositDisclosures: DepositDisclosureData[];
}

const initialState: UserState = {
  details: {
    customerId: "",
    name: "",
    firstName: "",
    lastName: "",
    dob: "",
    email: "",
    phone: "",
    loginCount: undefined,
  },
  is2FAEnabled: false,
  broker: {
    email: "",
    name: "",
    phoneNumberAU: "",
    phoneNumberUS: "",
    countryCode: "",
    imgUrl: "",
  },
  defaultCurrency: {
    code: "USD",
  },
  kycSteps: {
    newApplicationForm: false,
    biometrics: false,
  },
  kycStatus: "",
  watchlist: [],
  referralSignUpCode: "",
  userHoldings: [],
  isCustomerTalkingWithBroker: false,
  reKycDetails: {
    reKyc: false,
    kycExpiryDate: "",
    recordType: "",
    formSubmitted: false,
    onfidoCheck: false,
    edd: false,
  },
  overview: { balance: 0, pending: 0 },
  isUnverified: false,
  isPendingVerification: false,
  raf: {
    tier: "bronze",
    referredAmount: 0,
    rafCode: "",
    rafOptedIn: false,
    reward: "2",
    OLD_RAF_SYSTEM: true,
    isLoadingRaf: true,
  },
  notifications: {
    notificationAmount: 0,
    currentNotifications: [],
    previousNotifications: [],
    refetchNotifications: false,
  },
  isSubmitted: false,
  salesforceDetails: {
    recordType: "",
    ssnRequired: false,
    einRequired: false,
    submitted: false,
  },
  burlingBank: false,
  emailCode: null,
  code2fa: null,
  isEmailCodeSuccess: false,
  is2FASuccess: false,
  accountCreateTime: null,
  address: {
    zip: "",
    country: "US",
    city: "",
    state: "",
    fullAddress: "",
    streetAddress: "",
  },
  loginRiskSet: false,
  accountLock: {
    locked: false,
    lockType: "",
    reason: "",
    timestamp: 0,
  },
  stateDisclosures: [],
  depositDisclosures: [],
};

export const selectWatchlistData = (state: { watchlist: any }) => state.watchlist;

export const reset2FAStateThunk = createAsyncThunk(
  "user/reset2FAState",
  async (_, { dispatch }) => {
    dispatch(reset2FAState());
  },
);

const UserSlice = createSlice({
  name: "user",
  initialState: initialState,
  reducers: {
    setUserData: (
      state,
      { payload }: PayloadAction<UserDetailsRes & { loginCount: number | undefined }>,
    ) => {
      state.details = payload;
    },
    setUserDefaultCurrency: (state, { payload }: PayloadAction<string>) => {
      state.defaultCurrency.code = payload;
    },
    setBroker: (state, { payload }: PayloadAction<BrokerRes>) => {
      state.broker = payload;
    },
    setIs2FAEnabled: (state, { payload }: PayloadAction<boolean>) => {
      state.is2FAEnabled = payload;
    },
    setKycSteps: (state, { payload }: PayloadAction<Partial<UpdateKycStepsDto> | undefined>) => {
      state.kycSteps = payload ?? initialState.kycSteps;
    },
    setWatchlist: (state, { payload }: PayloadAction<IWatchListDataRes[]>) => {
      state.watchlist = payload;
      // Clear previous state when setting watchlist from API
      state.watchlistPreviousState = undefined;
    },
    addToWatchlistOptimistic: (
      state,
      {
        payload,
      }: PayloadAction<{
        code: string;
        currencyConfig?: CurrencyConfigRes;
      }>,
    ) => {
      // Check if already in watchlist (case-insensitive comparison)
      // Check both real items and temp items to prevent duplicates
      const normalizedPayloadCode = payload.code.toUpperCase();
      const existingIndex = state.watchlist?.findIndex((item) => {
        const itemCode = item.currencyConfig.code.toUpperCase();
        const isMatchingCode = itemCode === normalizedPayloadCode;
        // Also check for temp ID to prevent duplicate optimistic adds
        const isTempItem =
          item._id?.startsWith("temp-") &&
          item._id.toUpperCase() === `TEMP-${normalizedPayloadCode.toLowerCase()}`;
        return isMatchingCode || isTempItem;
      });
      if (existingIndex !== undefined && existingIndex >= 0) {
        // Already exists (either as real item or temp item), don't add again
        return;
      }

      // Create minimal currencyConfig if not provided
      // Use logoUrl from currencyConfig if available, otherwise use empty string
      // The formatWatchlistData function will handle empty logoUrl by using IconMapping as fallback
      const logoUrl = payload.currencyConfig?.logoUrl || "";
      const currencyConfig: CurrencyConfigRes =
        payload.currencyConfig ||
        ({
          code: payload.code,
          name: payload.code,
          displayName: payload.code,
          logoUrl: logoUrl,
          type: "Crypto",
          networkName: "",
          formatName: payload.code,
          inactive: false,
          memo: false,
          depositable: false,
          permissions: { buy: false, sell: false, deposit: false, withdraw: false },
        } as CurrencyConfigRes);

      // Create minimal watchlist item for optimistic update
      // Use normalized code for temp ID to ensure consistent matching during merge
      const normalizedCode = payload.code.toUpperCase();
      const optimisticItem: IWatchListDataRes = {
        _id: `temp-${normalizedCode}`,
        id: `temp-${normalizedCode}`,
        currencyConfig: currencyConfig,
        // Ensure logoUrl is set from currencyConfig for proper icon display
        // formatWatchlistData uses coin.logoUrl to set coin.icon
        logoUrl: currencyConfig.logoUrl || logoUrl || "",
        name: currencyConfig.displayName || currencyConfig.name || payload.code,
        price: "0",
        change1d: 0,
        circulating_supply: 0,
        market_cap: "0",
        tradingVolume: 0,
        sparkline: [],
      };

      // Add to watchlist
      if (!state.watchlist) {
        state.watchlist = [optimisticItem];
      } else {
        state.watchlist = [...state.watchlist, optimisticItem];
      }
    },
    removeFromWatchlistOptimistic: (state, { payload }: PayloadAction<string>) => {
      // Store previous state for potential rollback
      if (state.watchlist) {
        state.watchlistPreviousState = [...state.watchlist];
      } else {
        state.watchlistPreviousState = [];
      }

      // Remove from watchlist by code (case-insensitive comparison)
      if (state.watchlist) {
        const normalizedPayload = payload.toUpperCase();
        state.watchlist = state.watchlist.filter(
          (item) => item.currencyConfig.code.toUpperCase() !== normalizedPayload,
        );
      }
    },
    mergeWatchlistItem: (state, { payload }: PayloadAction<IWatchListDataRes>) => {
      // Merge a single watchlist item (from v2 API response) into existing watchlist
      // This replaces the optimistic update with real server data
      if (!state.watchlist) {
        state.watchlist = [payload];
        state.watchlistPreviousState = undefined;
        return;
      }

      // Find and replace the optimistic item with real data (case-insensitive comparison)
      const normalizedPayloadCode = payload.currencyConfig.code.toUpperCase();

      // Look for existing item by code (could be temp or real item)
      const existingIndex = state.watchlist.findIndex((item) => {
        const itemCode = item.currencyConfig.code.toUpperCase();
        const isMatchingCode = itemCode === normalizedPayloadCode;
        // Also check for temp ID (normalized to uppercase for consistency)
        const isTempItem =
          item._id?.startsWith("temp-") &&
          item._id.toUpperCase() === `TEMP-${normalizedPayloadCode}`;
        return isMatchingCode || isTempItem;
      });

      if (existingIndex >= 0) {
        // Replace optimistic item with real data
        state.watchlist[existingIndex] = payload;
      } else {
        // Check if item already exists as a non-temp item (prevent duplicates)
        const alreadyExists = state.watchlist.some(
          (item) =>
            !item._id?.startsWith("temp-") &&
            item.currencyConfig.code.toUpperCase() === normalizedPayloadCode,
        );

        if (!alreadyExists) {
          // Add if not found and not duplicate (shouldn't happen, but safe fallback)
          state.watchlist.push(payload);
        }
        // If duplicate exists, don't add - the server data is already there
      }

      // Clear previous state after successful merge
      state.watchlistPreviousState = undefined;
    },
    setReferralSignUpCode: (state, { payload }: PayloadAction<string>) => {
      state.referralSignUpCode = payload;
    },
    setIsCustomerTalkingWithBroker: (state, { payload }: PayloadAction<boolean>) => {
      state.isCustomerTalkingWithBroker = payload;
    },
    setUserHoldings: (state, { payload }: PayloadAction<HoldingRes[]>) => {
      state.userHoldings = payload;
    },
    setIkycDetails: (state, { payload }: PayloadAction<ReKycRes>) => {
      state.reKycDetails = payload;
    },
    setRekycOnfidoCheck: (state, { payload }: PayloadAction<boolean>) => {
      state.reKycDetails.onfidoCheck = payload;
    },
    setUserOverview: (state, { payload }: PayloadAction<IOverview>) => {
      state.overview = payload;
    },
    setDefaultTradeFee: (state, { payload }: PayloadAction<number>) => {
      state.defaultTradeFee = payload;
    },
    setIsUserVerified: (state, { payload }: PayloadAction<boolean>) => {
      state.isUnverified = payload;
    },
    setUserRaf: (state, { payload }: PayloadAction<Partial<IRaf>>) => {
      state.raf = { ...state.raf, ...payload };
    },
    setNotifications: (state, { payload }: PayloadAction<Partial<INotifications>>) => {
      state.notifications = { ...state.notifications, ...payload };
    },
    setCurrentNotificationToPrevious: (state, { payload }: PayloadAction<NotificationRes>) => {
      state.notifications.previousNotifications = [
        payload,
        ...state.notifications.previousNotifications,
      ];
    },
    setIsSubmitted: (state, { payload }: PayloadAction<boolean>) => {
      state.isSubmitted = payload;
    },
    setSalesforceDetails: (state, { payload }: PayloadAction<SalesforceDetailsRes>) => {
      state.salesforceDetails = payload;
    },
    setEmailCode: (state: UserState, action: PayloadAction<string>): void => {
      state.emailCode = action.payload;
    },
    set2FACode: (state: UserState, action: PayloadAction<string>): void => {
      state.code2fa = action.payload;
    },
    setIsEmailCodeSuccess: (state: UserState, action: PayloadAction<boolean>): void => {
      state.isEmailCodeSuccess = action.payload;
    },
    setIs2FASuccess: (state: UserState, action: PayloadAction<boolean>): void => {
      state.is2FASuccess = action.payload;
    },
    reset2FAState: (state: UserState): void => {
      state.is2FASuccess = false;
      state.isEmailCodeSuccess = false;
      state.code2fa = null;
      state.emailCode = null;
    },
    setAccountCreateTime: (state, { payload }: PayloadAction<number>) => {
      state.accountCreateTime = payload;
    },
    setUserAddress: (state, { payload }: PayloadAction<AddressRes>) => {
      state.address = payload;
    },
    setLoginRiskSet: (state, action: PayloadAction<boolean>): void => {
      state.loginRiskSet = action.payload;
    },
    setKycStatus: (state, action: PayloadAction<string>): void => {
      state.kycStatus = action.payload;
    },
    setIsPendingVerification: (state, action: PayloadAction<boolean>): void => {
      state.isPendingVerification = action.payload;
    },
    setAccountLock: (state, action: PayloadAction<AccountLockRes>): void => {
      state.accountLock = action.payload;
    },
    setUserStateDisclosures: (state, action: PayloadAction<StateDisclosureData[]>): void => {
      state.stateDisclosures = action.payload;
    },
    setUserDepositDisclosures: (state, action: PayloadAction<DepositDisclosureData[]>): void => {
      state.depositDisclosures = action.payload;
    },
  },
});

export const {
  setUserData,
  setUserDefaultCurrency,
  setBroker,
  setReferralSignUpCode,
  setIs2FAEnabled,
  setWatchlist,
  addToWatchlistOptimistic,
  removeFromWatchlistOptimistic,
  mergeWatchlistItem,
  setKycSteps,
  setIsCustomerTalkingWithBroker,
  setUserHoldings,
  setIkycDetails,
  setKycStatus,
  setRekycOnfidoCheck,
  setUserOverview,
  setDefaultTradeFee,
  setIsUserVerified,
  setUserRaf,
  setNotifications,
  setCurrentNotificationToPrevious,
  setIsSubmitted,
  setEmailCode,
  setIsEmailCodeSuccess,
  setIs2FASuccess,
  set2FACode,
  setSalesforceDetails,
  reset2FAState,
  setAccountCreateTime,
  setUserAddress,
  setLoginRiskSet,
  setIsPendingVerification,
  setAccountLock,
  setUserStateDisclosures,
  setUserDepositDisclosures,
} = UserSlice.actions;

export const useUserSlice = () => useAppSelector((state) => state.user);

export default UserSlice;
